import React, { useEffect, useState, useCallback } from "react";
import { BarChart3, RefreshCw, Eye, Users, Globe } from "lucide-react";
import { adminAnalytics } from "../../lib/api";

const StatTile = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="border border-[#2D2A26] bg-[#0F0F0F] px-4 py-3"
  >
    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
      {label}
    </div>
    <div className="font-display text-2xl text-[#E4DFD3] mt-1">{value ?? "—"}</div>
  </div>
);

const Row = ({ label, value, max }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value * 100) / max)) : 0;
  return (
    <div className="flex items-center gap-3 mb-1.5">
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[#E4DFD3] truncate">{label}</div>
        <div className="h-1 bg-[#1a1a1a] mt-1 relative">
          <div
            className="absolute inset-y-0 left-0 bg-[#D92D20]/70"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <div className="font-mono text-xs text-[#CBB892] tabular-nums w-10 text-right">
        {value}
      </div>
    </div>
  );
};

const Section = ({ title, children, count }) => (
  <div className="border border-[#2D2A26] bg-[#131313] p-5">
    <div className="flex items-baseline justify-between mb-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
        // {title}
      </div>
      {count !== undefined && (
        <div className="font-mono text-[10px] text-[#757065]">{count}</div>
      )}
    </div>
    {children}
  </div>
);

const Sparkline = ({ daily }) => {
  if (!daily?.length) return null;
  const max = Math.max(1, ...daily.map((d) => d.count));
  return (
    <div className="flex items-end gap-1 h-16 mt-2" data-testid="analytics-sparkline">
      {daily.map((d) => {
        const h = Math.max(2, Math.round((d.count * 100) / max));
        return (
          <div
            key={d.date}
            title={`${d.date}: ${d.count}`}
            className="flex-1 bg-[#D92D20]/60 hover:bg-[#D92D20] transition-colors"
            style={{ height: `${h}%` }}
          />
        );
      })}
    </div>
  );
};

export const AnalyticsPanel = ({ token, userToken = null }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d = days) => {
    setLoading(true);
    try {
      const res = await adminAnalytics(token, d, userToken);
      setData(res);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, userToken, days]);

  useEffect(() => { if (token || userToken) load(days); }, [token, userToken, days, load]);

  if (!data && !loading) {
    return (
      <div className="border border-[#2D2A26] bg-[#131313] p-6 mb-8" data-testid="analytics-panel-empty">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065]">
          Analytics unavailable
        </div>
      </div>
    );
  }

  const maxDossier = data?.dossier_views?.[0]?.count || 0;
  const maxPath = data?.top_paths?.[0]?.count || 0;
  const maxRef = data?.referrers?.[0]?.count || 0;

  return (
    <section className="mb-12" data-testid="analytics-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <BarChart3 size={14} /> // SITE ANALYTICS
          </div>
          <h2 className="font-display text-3xl text-[#E4DFD3]">Traffic & Engagement</h2>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="analytics-days-select"
            className="bg-[#0F0F0F] border border-[#2D2A26] text-[#E4DFD3] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2"
          >
            <option value={7}>last 7 days</option>
            <option value={30}>last 30 days</option>
            <option value={90}>last 90 days</option>
          </select>
          <button
            onClick={() => load(days)}
            data-testid="analytics-refresh-btn"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Top-line tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatTile testid="stat-pageviews"     label="Total Pageviews"  value={data?.total_pageviews} />
        <StatTile testid="stat-sessions"      label="Unique Sessions"  value={data?.unique_sessions} />
        <StatTile testid="stat-home"          label="Home Visits"      value={data?.key_pages?.home} />
        <StatTile testid="stat-archive"       label="Archive Visits"   value={data?.key_pages?.archive} />
        <StatTile testid="stat-submit"        label="Submit Visits"    value={data?.key_pages?.submit} />
        <StatTile testid="stat-spotlight"     label="Spotlight Visits" value={data?.key_pages?.spotlight} />
        <StatTile testid="stat-direct"        label="Direct Visits"    value={data?.direct_visits} />
        <StatTile testid="stat-devices"       label="Mobile / Desktop"
          value={`${data?.devices?.mobile || 0} / ${data?.devices?.desktop || 0}`} />
      </div>

      {/* Daily sparkline */}
      <Section title="DAILY PAGEVIEWS" count={`${data?.daily?.length || 0}d`}>
        <Sparkline daily={data?.daily || []} />
        <div className="flex justify-between font-mono text-[10px] text-[#757065] mt-2">
          <span>{data?.daily?.[0]?.date || ""}</span>
          <span>{data?.daily?.[data?.daily?.length - 1]?.date || ""}</span>
        </div>
      </Section>

      {/* Three columns: dossiers / paths / referrers */}
      <div className="grid md:grid-cols-3 gap-3 mt-3">
        <Section title="DOSSIER VIEWS" count={data?.dossier_views?.length || 0}>
          {(data?.dossier_views || []).slice(0, 12).map((d) => (
            <Row key={d.slug} label={d.title} value={d.count} max={maxDossier} />
          ))}
          {!data?.dossier_views?.length && (
            <div className="text-[#757065] text-xs italic">No dossier views yet.</div>
          )}
        </Section>

        <Section title="TOP PATHS" count={data?.top_paths?.length || 0}>
          {(data?.top_paths || []).slice(0, 12).map((p) => (
            <Row key={p.path} label={p.path} value={p.count} max={maxPath} />
          ))}
        </Section>

        <Section title="REFERRERS" count={data?.referrers?.length || 0}>
          {(data?.referrers || []).slice(0, 12).map((r) => (
            <Row key={r.host} label={r.host} value={r.count} max={maxRef} />
          ))}
          {!data?.referrers?.length && (
            <div className="text-[#757065] text-xs italic">No external referrers yet.</div>
          )}
        </Section>
      </div>
    </section>
  );
};

export default AnalyticsPanel;
